import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import communicationService from '../../services/communicationService';

/**
 * RecipientInput component for managing multiple email/phone recipients
 * Displays recipients as chips with remove buttons
 * Automatically normalizes phone numbers to E.164 format (+1XXXXXXXXXX)
 *
 * @param {Array} recipients - Array of recipient strings (emails or normalized phone numbers)
 * @param {Function} onChange - Callback when recipients change
 * @param {String} type - 'email' or 'phone' for validation and placeholder
 * @param {String} label - Label for the input field
 * @param {String} placeholder - Placeholder text
 * @param {Boolean} disabled - Whether the input is disabled
 */
const RecipientInput = ({
  recipients = [],
  onChange,
  type = 'email',
  label,
  placeholder,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const validateRecipient = (value) => {
    if (!value.trim()) {
      return 'Recipient cannot be empty';
    }

    if (type === 'email') {
      // Basic email validation
      if (!value.includes('@') || !value.includes('.')) {
        return 'Please enter a valid email address';
      }
    } else if (type === 'phone') {
      // Check for extensions first - provide specific error
      if (communicationService.hasExtension(value)) {
        return 'Phone extensions are not supported. Please enter the main phone number only.';
      }

      // Use the communicationService validation
      if (!communicationService.isValidPhoneNumber(value)) {
        const digits = value.replace(/\D/g, '');
        if (digits.length < 7) {
          return 'Phone number must have at least 7 digits';
        } else if (digits.length > 15) {
          return 'Phone number cannot exceed 15 digits';
        }

        // Check for invalid area codes
        if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
          const areaCode = digits.length === 10 ? digits.substring(0, 3) : digits.substring(1, 4);
          if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
            return `Invalid area code: ${areaCode}. US area codes cannot start with 0 or 1`;
          }
        }

        return 'Please enter a valid phone number';
      }
    }

    return null;
  };

  const handleAddRecipient = () => {
    const trimmedValue = inputValue.trim();
    const validationError = validateRecipient(trimmedValue);

    if (validationError) {
      setError(validationError);
      return;
    }

    // Normalize phone numbers to E.164 format
    let recipientToAdd = trimmedValue;
    if (type === 'phone') {
      const normalized = communicationService.normalizePhoneNumber(trimmedValue);
      if (!normalized) {
        setError('Failed to normalize phone number');
        return;
      }
      recipientToAdd = normalized;
    }

    // Check for duplicates (after normalization for phones)
    if (recipients.includes(recipientToAdd)) {
      setError('This recipient has already been added');
      return;
    }

    // Add the recipient (normalized for phones, as-is for emails)
    onChange([...recipients, recipientToAdd]);
    setInputValue('');
    setError('');
  };

  const handleRemoveRecipient = (indexToRemove) => {
    onChange(recipients.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddRecipient();
    } else if (e.key === 'Backspace' && !inputValue && recipients.length > 0) {
      // Remove last recipient if backspace is pressed on empty input
      handleRemoveRecipient(recipients.length - 1);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (error) {
      setError('');
    }
  };

  return (
    <div>
      {label && (
        <label className="text-sm font-medium text-gray-700 mb-2 block">
          {label}
        </label>
      )}

      <div className="w-full border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-yellow-500">
        {/* Recipient chips */}
        {recipients.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 border-b border-gray-200">
            {recipients.map((recipient, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded-md text-sm"
              >
                <span>
                  {type === 'phone'
                    ? communicationService.formatPhoneNumber(recipient)
                    : recipient
                  }
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveRecipient(index)}
                  disabled={disabled}
                  className="text-yellow-600 hover:text-gray-800 disabled:opacity-50"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input field with add button */}
        <div className="flex items-center gap-2 p-2">
          <input
            type={type === 'email' ? 'email' : 'tel'}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || (type === 'email' ? 'email@example.com' : '+1 (555) 123-4567')}
            disabled={disabled}
            className="flex-1 outline-none disabled:bg-gray-50"
          />
          <button
            type="button"
            onClick={handleAddRecipient}
            disabled={disabled || !inputValue.trim()}
            className="text-yellow-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            title="Add recipient"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-500 mt-1">
        Press Enter or click + to add. Press Backspace on empty input to remove last recipient.
      </p>
    </div>
  );
};

export default RecipientInput;
